import React                 from "react";
import PropTypes             from "prop-types";

import PictureCrop           from "./PictureCrop";
import PictureUpload         from "./PictureUpload";
import { AlertBox }          from "sp-react-commons";

import Svg                   from "../../../../svg/Svg";

let DROPSTYLE = {
    width: '100%',
    maxWidth: '100%'
};

export default class PicturePreview extends React.Component {

    static defaultProps = {
        height              : 100,
        width               : 100,
        onUpdatePicture     : () => {},
        role                : "main",
        cropAspectRatio     : 1,
        minCropWidth        : 0,
        maxFileSize         : 7 * 1024 * 1024,
        defaultPictureText  : "Picture",
        defaultPictureStyle : {},
        errors              : {
            DEFAULT_ERROR               : "Picture is corrupted or in the wrong format",
            BAD_SIZE                    : "Max file size exceeded"
        },
        translations        : {
            CROP_PICTURE                : "Change picture",
            UPLOAD_PICTURE              : "Upload",
            PREVIEW_PICTURE             : "Preview picture"
        },
        uploadClassName     : "u-hover-cursor"
    };

    static propTypes = {
        picture             : PropTypes.object,               // Json object representation of a Picture
        pictureUri          : PropTypes.string,               // Uri of the picture
        pictureRawUri       : PropTypes.string,               // Raw Uri of the picture used to crop original picture
        defaultPictureUri   : PropTypes.string,               // Default picture uri in case of pictureUri not defined
        defaultPictureText  : PropTypes.string,               // Default picture text in case of defaultPictureUri is not defined
        defaultPictureStyle : PropTypes.object,               // Default picture style in case of defaultPictureUri is not defined
        height              : PropTypes.number,
        width               : PropTypes.number,
        onUpdatePicture     : PropTypes.func,
        role                : PropTypes.string,
        cropAspectRatio     : PropTypes.number,
        minCropWidth        : PropTypes.number,
        uploadPicture       : PropTypes.func.isRequired,
        maxFileSize         : PropTypes.number,
        errors              : PropTypes.object,
        translations        : PropTypes.object,
        uploadClassName     : PropTypes.string
    };

    constructor(props) {
        super(props);
        this.state = this.initialState();
    }

    initialState = () => ({
        isEditing: false,
        picture: this.props.picture,
        pictureRawUri: this.props.pictureRawUri,
        isValidCrop: true
    });

    onUpdatePicture = () => this.props.onUpdatePicture({
        picture: this.state.picture,
        isValidCrop: this.state.isValidCrop
    });

    onError = (error) => error ?
        AlertBox.Actions.displayAlertError({ message : error }) :
        AlertBox.Actions.displayAlertError({ message : this.props.errors.DEFAULT_ERROR });

    onCropPicture = (cropData, isValidCrop) => {
        let picture = this.state.picture;
        if(isValidCrop)
            picture.cropCoordinates = [cropData.x1, cropData.y1, cropData.x2, cropData.y2];

        this.setState({
            picture: picture,
            isValidCrop: isValidCrop
        }, this.onUpdatePicture);
    };

    onAddPicture = (pictureBlob, pictureDataUrl) => {
        this.props.uploadPicture(pictureBlob).then( (picture) => {
            AlertBox.Actions.hideAlert();
            this.setState({
                picture: {
                    id: picture.id,
                    uuid: picture.uuid,
                    role: this.props.role,
                    title: picture.title,
                    credit: picture.credit,
                    height: picture.height,
                    width: picture.width,
                    cropCoordinates: [0, 0, 0, 0]
                },
                pictureRawUri: pictureDataUrl,
                isEditing: true
            }, this.onUpdatePicture);
        });
    };

    onVerifyPictureFile = (file, img) => {
        if(file.size > this.props.maxFileSize || (img.width < this.props.minCropWidth || img.height < this.props.minCropWidth)) {
            this.onError(this.props.errors.BAD_SIZE);
            return false;
        }
        return true;
    };

    onClickCropPicture = () => this.setState({ isEditing: !this.state.isEditing });
    onClickPreviewPicture = () => this.setState({ isEditing: false });

    cropPicturePreview = (canvas) => {
        if(!canvas || !this.state.pictureRawUri)
            return;

        let context = canvas.getContext('2d');
        let imageObj = new Image();

        imageObj.onload = () => {
            // Draw cropped picture
            let cropCoordinates = this.state.picture.cropCoordinates;
            let sourceX = cropCoordinates[0];
            let sourceY = cropCoordinates[1];
            let sourceWidth = cropCoordinates[2] - cropCoordinates[0];
            let sourceHeight = cropCoordinates[3] - cropCoordinates[1];
            let destWidth = this.props.width;
            let destHeight = this.props.height;
            let destX = 0;
            let destY = 0;
            context.drawImage(imageObj, sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight);
        };

        // Update canvas with new picture preview
        imageObj.src = this.state.pictureRawUri;
    };

    renderEditor = () => (
        <PictureCrop aspect={this.props.cropAspectRatio}
                     minCropWidth={this.props.minCropWidth}
                     picture={this.state.picture}
                     pictureUri={this.state.pictureRawUri}
                     onChange={this.onCropPicture}/>
    );

    renderDefaultPicture = () => (
        <PictureUpload dropStyle={DROPSTYLE}
                       onChange={this.onAddPicture}
                       onVerifyFile={this.onVerifyPictureFile}
                       onError={this.onError}
                       className={this.props.uploadClassName}>
            <div className="picture-upload-crop-preview__default-picture-wrapper u-full-width" style={this.props.defaultPictureStyle}>
                <div className="picture-upload-crop-preview__default-picture">
                    <Svg id="fa-camera" className="picture-upload-crop-preview__default-upload-icon"/>
                    <div className="picture-upload-crop-preview__default-picture-text">{ this.props.defaultPictureText }</div>
                </div>
            </div>
        </PictureUpload>
    );

    renderPicturePreview = () => {
        if(this.state.pictureRawUri)
            return <canvas className="u-img-full-width" ref={(canvas) => this.cropPicturePreview(canvas)} width={this.props.width} height={this.props.height}/>;
        else if(this.props.pictureUri)
            return <img className="u-img-full-width" src={this.props.pictureUri}/>;
        else if(this.props.defaultPictureUri)
            return this.renderDefaultPicture(); // TODO RCH : implements defaultPictureUri management (use <PictureUpload/>)
        else
            return this.renderDefaultPicture();
    };

    renderButtons = () => (
        <div className="picture-upload-crop-preview__buttons">
            <PictureUpload dropStyle={DROPSTYLE}
                           onChange={this.onAddPicture}
                           onVerifyFile={this.onVerifyPictureFile}
                           onError={this.onError}
                           className={this.props.uploadClassName}>
                <button className="picture-upload-crop-preview__button mywort-btn mywort-btn--outlined mywort-btn--small u-text-uppercase">
                    <Svg id="fa-upload" className="mywort-btn__icon"/> { this.props.translations.UPLOAD_PICTURE }
                </button>
            </PictureUpload>
            { this.state.isEditing ?
                <button onClick={this.onClickPreviewPicture} className="picture-upload-crop-preview__button mywort-btn mywort-btn--outlined mywort-btn--small u-text-uppercase">
                    <Svg id="icon-picture" className="mywort-btn__icon"/> { this.props.translations.PREVIEW_PICTURE }
                </button>
                :
                <button onClick={this.onClickCropPicture} className="picture-upload-crop-preview__button mywort-btn mywort-btn--outlined mywort-btn--small u-text-uppercase">
                    <Svg id="fa-crop" className="mywort-btn__icon"/> { this.props.translations.CROP_PICTURE }
                </button>
            }
        </div>
    );

    render = () => (
        <div>
            { this.state.isEditing ? this.renderEditor() : this.renderPicturePreview() }
            { this.state.pictureRawUri ? this.renderButtons() : null }
        </div>
    );
}



// WEBPACK FOOTER //
// ./js/mywort/commons/components/picture-upload-crop/PicturePreview.jsx